import { Check, CloudUpload, Error } from '@mui/icons-material';
import { Button, TextField, Typography } from '@mui/material';
import type { FormikProps } from 'formik';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { DataSourceKind } from '~/generated/graphql';
import { toDataUri } from '~/lib/file';

type UploadFormValues = {
  dataUri: string;
  foreignIdName: string;
  name: string;
};

type UploadFormProps = {
  formik: FormikProps<UploadFormValues>;
  selectedKind: DataSourceKind | undefined;
};

export const UploadForm = ({ formik, selectedKind }: UploadFormProps) => {
  const { t } = useTranslation();
  const { getInputProps, open: selectFile } = useDropzone({
    accept:
      selectedKind === DataSourceKind.Csv
        ? 'text/csv'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    maxSize: 5_000_000, // 5mb
    multiple: false,
    onDrop: async (accepted, rejected, _event) => {
      if (rejected.length > 0) {
        formik.setErrors({ dataUri: rejected[0].errors[0].message });
        formik.setTouched({ dataUri: true });
        return;
      }
      const dataUri = await toDataUri(accepted[0]);
      await formik.setFieldValue('dataUri', dataUri, true);
    },
  });

  return (
    <form id="new-data-source-form" onSubmit={formik.handleSubmit}>
      <label>
        <span className="label required">{t('pages.data_sources.upload_form.name_label')}</span>
        <TextField
          autoFocus
          error={formik.touched.name && !!formik.errors.name}
          fullWidth
          helperText={(formik.touched.name && formik.errors.name) || ' '}
          name="name"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          size="small"
          value={formik.values.name}
        />
      </label>
      <label>
        <span className="label">{t('pages.data_sources.upload_form.unique_id_label')}</span>
        <TextField
          error={formik.touched.foreignIdName && !!formik.errors.foreignIdName}
          fullWidth
          helperText={t('pages.data_sources.upload_form.unique_id_help')}
          name="foreignIdName"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          size="small"
          value={formik.values.foreignIdName}
        />
      </label>
      <label>
        <input {...getInputProps()} />
        <div style={{ alignItems: 'center', display: 'flex', marginTop: '8px' }}>
          <Button
            color="primary"
            startIcon={<CloudUpload />}
            onClick={selectFile}
            variant="contained"
          >
            {t('pages.data_sources.upload_form.choose_file', { kind: selectedKind })}
          </Button>
          {formik.touched.dataUri && formik.errors.dataUri && (
            <>
              <Error color="error" sx={{ marginX: 1 }} />
              <Typography color="error">
                {t('pages.data_sources.upload_form.file_size_error')}
              </Typography>
            </>
          )}
          {formik.values.dataUri && <Check color="success" sx={{ marginX: 1 }} />}
        </div>
      </label>
    </form>
  );
};
